import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from '../analytics';

function ShareButton({ variant = "default", openUpward = false }) {
  const [copied, setCopied] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const dropdownRef = useRef(null);

  const currentUrl = window.location.href;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      trackEvent('link_copied', {
        url: currentUrl,
        source: 'share_button',
        method: 'clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      trackEvent('link_copied', {
        url: currentUrl,
        source: 'share_button',
        method: 'fallback',
      });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEmailShare = (e) => {
    e.preventDefault();
    if (email) {
      const subject = encodeURIComponent("PM Dashboard - Elizabeth Blege");
      const body = encodeURIComponent(
        `I thought you might find this interesting:\n\n${currentUrl}`
      );
      trackEvent('shared_via_email', {
        url: currentUrl,
        recipient_email: email,
      });
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      setShowEmailForm(false);
      setEmail("");
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmailForm(false);
      }
    };

    if (showEmailForm) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showEmailForm]);

  const isPrimary = variant === "primary";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          const wasOpen = showEmailForm;
          setShowEmailForm(!showEmailForm);
          if (!wasOpen) {
            trackEvent('share_button_opened', {
              variant: variant,
            });
          }
        }}
        className={`flex items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
          isPrimary
            ? "border-primary-500 dark:border-primary-400 bg-primary-500 dark:bg-primary-600 text-white hover:bg-primary-600 dark:hover:bg-primary-700 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-400"
            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:ring-primary-500 dark:focus-visible:ring-primary-400"
        } focus-visible:ring-offset-gray-900`}
        aria-label="Share dashboard"
        aria-expanded={showEmailForm}
      >
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
        <span>Share</span>
      </button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {showEmailForm && (
          <motion.div
            initial={{ opacity: 0, y: openUpward ? 10 : -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: openUpward ? 10 : -10 }}
            className={`absolute right-0 z-10 w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-lg ${
              openUpward ? "bottom-full mb-2" : "top-full mt-2"
            }`}
          >
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <svg
                className="h-5 w-5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span>{copied ? "Copied!" : "Copy link"}</span>
            </button>

            <form onSubmit={handleEmailShare} className="space-y-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-primary-400/20"
              />
              <button
                type="submit"
                className="w-full rounded-lg bg-primary-500 dark:bg-primary-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 dark:hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                Send Email
              </button>
            </form>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ShareButton;
